import { Navigate, createBrowserRouter, useParams, useSearchParams } from 'react-router-dom'
import { ProtectedRouts } from './protected'
import EnterForm from '../pages/EnterPages/EnterForm'
import LoginForm from '../components/Forms/loginForm'
import RegisterForm from '../components/Forms/registerForm'
import RegisterConfirmed from '../components/Forms/registerForm/registerConfirmed'
import CheckEmail from '../components/Forms/registerForm/checkEmail'
import PlanSelection from '../pages/PlanSelection'
import ChangePlan from '../pages/ChangePlan'
import MyInfo from '../pages/MyInfo'
import Patients from '../pages/Patients'
import Home from '../pages/Home'
import PatientCard from '../components/PatientsBox/PatientProfile'
import PatientsList from '../components/PatientsBox/PatientsList'
import NotFound from '../pages/NotFound'
import Whiteboard from '../components/WhiteBoard'
import CollaboratoryPage from '../pages/Collaboratory'
import UserModuleAdmin from '../pages/admin/UserModuleAdmin'
import PilotsAdmin from '../pages/admin/PilotsAdmin'
import LabPage from '../pages/Lab'
import SpecialtyAnalysisPage from '../pages/Lab/SpecialtyAnalysis'
import BlotRegistryPage from '../pages/Lab/BlotRegistry'
import MedInsight from '../pages/MedInsight'
import LegalDocumentPage from '../pages/Legal/LegalDocumentPage'
import GuestCaseAccess from '../pages/GuestCaseAccess'

// Component to redirect /blot-analysis/:caseId to /lab/blot-analysis/:caseId
function BlotAnalysisRedirect() {
  const { caseId } = useParams<{ caseId: string }>()
  return <Navigate to={`/lab/blot-analysis${caseId ? `/${caseId}` : ''}`} replace />
}

// Registration is invitation-only. The /register form is reachable ONLY with a
// valid ?invitationToken=… — the link carried by the "invitation-to-register"
// email (buildInviteUrl on the backend). Without a token there is no public
// sign-up, so visitors are sent to the login page.
function InvitationOnlyRegister() {
  const [params] = useSearchParams()
  return params.get('invitationToken')
    ? <RegisterForm />
    : <Navigate to="/login" replace />
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/home" replace />,
  },
  {
    element: <EnterForm />,
    children: [
      {
        path: 'login',
        element: <LoginForm />,
      },
      {
        // Invitation-only sign-up: renders the form with a valid ?invitationToken,
        // otherwise redirects to /login (no public self-registration). See
        // InvitationOnlyRegister above.
        path: 'register',
        element: <InvitationOnlyRegister />,
      },
      {
        path: 'check-email',
        element: <CheckEmail />,
      },
      {
        path: 'confirm-email',
        element: <RegisterConfirmed />,
      },
    ],
  },
  {
    // Public legal pages — reachable without authentication (footer, sign-up).
    path: '/legal/:slug',
    element: <LegalDocumentPage />,
  },
  {
    // Public, no-account guest viewer for a shared case — see GuestCaseAccess.
    path: '/partage/:shareToken',
    element: <GuestCaseAccess />,
  },
  {
    path: '/select-plan',
    element: <PlanSelection />,
  },
  {
    path: '/select-modules',
    element: <Navigate to="/change-plan" replace />,
  },
  {
    element: <ProtectedRouts />,
    children: [
      {
        path: '/my-info',
        element: <MyInfo />,
      },
      {
        path: '/change-plan',
        element: <ChangePlan />,
      },
      {
        path: '/home',
        element: <Home />,
      },
      {
        path: '/patients',
        children: [
          {
            element: <Patients />,
            children: [
              {
                path: '',
                element: <PatientsList />,
              },
            ],
          },
          {
            path: ':id',
            element: <PatientCard />,
            children: [
              {
                path: 'diagnostics/:diagnosticId',
                element: <Whiteboard />,
              },
            ],
          },
        ],
      },
      {
        path: '/myCollaboratory',
        element: <CollaboratoryPage />,
      },
      {
        path: '/lab',
        element: <LabPage />,
      },
      {
        // Le registre d'évaluation : tous les cas, filtres + accord agrégé.
        path: '/lab/registry',
        element: <BlotRegistryPage />,
      },
      {
        path: '/lab/:specialtyId',
        element: <SpecialtyAnalysisPage />,
      },
      {
        path: '/lab/:specialtyId/:caseId',
        element: <SpecialtyAnalysisPage />,
      },
      {
        path: '/blot-analysis',
        element: <BlotAnalysisRedirect />,
      },
      {
        path: '/blot-analysis/:caseId',
        element: <BlotAnalysisRedirect />,
      },
      {
        path: '/literature',
        element: <MedInsight />,
      },
      {
        path: '/literature/:projectId',
        element: <MedInsight />,
      },
      {
        path: '/admin',
        element: <UserModuleAdmin />,
      },
      {
        path: '/admin/pilots',
        element: <PilotsAdmin />,
      },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
])
